import React, { useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CreditCard,Tent, Shield, Zap, CheckCircle, Mountain, Trees, Star } from "lucide-react";

const SubscribePage = () => {
  const { userId } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubscribe = async () => {
    if (!userId) {
      setError("User ID is missing.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/create-subscription-session/${userId}`
      );

      const { url } = response.data;

      if (url) {
        window.location.href = url;
      } else {
        setError("Failed to get checkout URL. Please try again.");
      }
    } catch (err) {
      console.error("Subscription error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background elements matching landing page */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-1/3 left-1/3 w-72 h-72 bg-emerald-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute bottom-1/3 right-1/3 w-72 h-72 bg-teal-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
        <div className="absolute top-2/3 left-2/3 w-72 h-72 bg-cyan-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-500"></div>
      </div>

      <div className="relative z-10 max-w-lg mx-auto w-full">
        <div className="bg-white/80 backdrop-blur-lg border border-white/20 rounded-3xl p-8 shadow-2xl">
          {/* Header with brand elements */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-3 rounded-2xl shadow-lg">
                <Tent className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                CampSite Checker
              </h2>
            </div>
            
            <div className="relative inline-block mb-4">
              <Star className="text-yellow-500 text-6xl mx-auto animate-pulse" />
              <div className="absolute inset-0 bg-yellow-400 rounded-full opacity-20 animate-ping"></div>
            </div>
            
            <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-4">
              Unlock Premium Features
            </h1>
            <p className="text-gray-600 text-lg">
              Get access to advanced campsite search and real-time availability
            </p>
          </div>

          {/* User ID Display */}
          {/* <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mb-6 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <User className="text-emerald-600" size={20} />
              <div>
                <p className="text-gray-500 text-sm">User ID</p>
                <p className="text-gray-900 font-mono text-lg">{userId || "Not found"}</p>
              </div>
            </div>
          </div> */}

          {/* Premium Features */}
          <div className="space-y-4 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">What's included:</h3>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-gray-700">
                <div className="bg-emerald-100 p-2 rounded-lg">
                  <Zap className="text-emerald-600" size={16} />
                </div>
                <span>Real-time campsite availability updates</span>
              </div>
              
              <div className="flex items-center gap-3 text-gray-700">
                <div className="bg-emerald-100 p-2 rounded-lg">
                  <Shield className="text-emerald-600" size={16} />
                </div>
                <span>Advanced search filters and alerts</span>
              </div>
              
              <div className="flex items-center gap-3 text-gray-700">
                <div className="bg-emerald-100 p-2 rounded-lg">
                  <Mountain className="text-emerald-600" size={16} />
                </div>
                <span>Access to 3000+ premium campgrounds</span>
              </div>
              
              <div className="flex items-center gap-3 text-gray-700">
                <div className="bg-emerald-100 p-2 rounded-lg">
                  <Trees className="text-emerald-600" size={16} />
                </div>
                <span>Priority customer support</span>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-6 mb-6 text-white text-center">
            <div className="text-3xl font-bold mb-2">$5.99/month</div>
            
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6 backdrop-blur-sm">
              <p className="text-red-600 text-center">{error}</p>
            </div>
          )}

          {/* Subscribe Button */}
          <button
            onClick={handleSubscribe}
            disabled={loading || !userId}
            className={`w-full relative group overflow-hidden rounded-2xl transition-all duration-300 ${
              loading || !userId 
                ? "opacity-50 cursor-not-allowed" 
                : "transform hover:scale-105 shadow-lg hover:shadow-2xl"
            }`}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-600 opacity-100 group-hover:opacity-90 transition-opacity duration-300"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-teal-400 opacity-0 group-hover:opacity-20 animate-pulse"></div>
            
            <div className="relative z-10 flex items-center justify-center gap-3 py-4 px-6">
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span className="text-white font-semibold text-lg">
                    Redirecting to Stripe...
                  </span>
                </>
              ) : (
                <>
                  <CreditCard className="text-white" size={24} />
                  <span className="text-white font-semibold text-lg">
                    Subscribe Now
                  </span>
                </>
              )}
            </div>
          </button>

          {/* Trust Badges */}
          <div className="mt-6 text-center">
            <p className="text-gray-500 text-sm mb-3">Secured by Stripe</p>
            <div className="flex justify-center gap-4 text-gray-400 text-xs">
              <div className="flex items-center gap-1">
                <Shield className="w-3 h-3" />
                <span>SSL Encrypted</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                <span>PCI Compliant</span>
              </div>
              <div className="flex items-center gap-1">
                <Star className="w-3 h-3" />
                <span>Money Back Guarantee</span>
              </div>
            </div>
          </div>

          {/* Nature elements for visual consistency */}
          <div className="flex items-center justify-center space-x-4 mt-6 opacity-60">
            <Mountain className="w-6 h-6 text-emerald-600" />
            <Trees className="w-6 h-6 text-teal-600" />
            <Mountain className="w-6 h-6 text-emerald-600" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscribePage;